"use client";

import { useRef } from "react";
import { BookHeart, Mountain, Smile } from "lucide-react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
gsap.registerPlugin(useGSAP, ScrollTrigger);
export default function Questions() {
  const container = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = container.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".spark-reveal", {
        y: 24
      });
      gsap.to(".spark-reveal", {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 85%",
          once: true
        }
      });
      gsap.set(".spark-card", {
        y: 30
      });
      gsap.to(".spark-card", {
        opacity: 1,
        y: 0,
        duration: 0.7,
        stagger: 0.12,
        ease: "power3.out",
        scrollTrigger: {
          trigger: ".spark-cards",
          start: "top 85%",
          once: true
        }
      });
      gsap.set(".spark-willow", {
        y: 16,
        rotate: -3
      });
      gsap.to(".spark-willow", {
        opacity: 1,
        y: 0,
        rotate: 0,
        duration: 0.9,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set([".spark-reveal", ".spark-card", ".spark-willow"], {
        opacity: 1,
        y: 0,
        rotate: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: container
  });
  const cards = [{
    icon: Smile,
    title: "What Are Kids Watching?",
    body: "Understand the content your kids consume and why it captures their attention.",
    bg: "bg-[var(--primary)]",
    textColor: "text-[var(--primary-foreground)]",
    bodyColor: "text-[var(--primary-foreground)] opacity-90",
    iconBg: "bg-[var(--background)]"
  }, {
    icon: Mountain,
    title: "What Do Parents Need to Know?",
    body: "Essential insights on digital safety, screen time limits, and healthy habits.",
    bg: "bg-[var(--secondary)]",
    textColor: "text-[var(--primary)]",
    bodyColor: "text-[var(--primary)] opacity-85",
    iconBg: "bg-[var(--background)]"
  }, {
    icon: BookHeart,
    title: "What Can Kids Do Instead?",
    body: "Creative offline activities to balance their day and spark new interests.",
    bg: "bg-[var(--background)] border border-[var(--border)]",
    textColor: "text-[var(--primary)]",
    bodyColor: "text-[var(--primary)] opacity-85",
    iconBg: "bg-[var(--muted)]"
  }];
  return <section data-section-id="33367" id="the-big-three-questions" className="bg-[var(--muted)] overflow-hidden dark ![padding:50px_0px_50px_0px]">
      <div ref={container} className="mx-auto max-w-[1240px] px-5 md:px-8">
        <div className="mx-auto max-w-[720px] text-center flex flex-col items-center">
          

          <svg className="spark-reveal mt-6 mb-8" width="140" height="14" viewBox="0 0 140 14" fill="none" aria-hidden="true" style={{
          opacity: 0
        }}>
            <path d="M2 8 C 25 2, 45 12, 70 7 S 115 2, 138 6" stroke="var(--primary)" strokeOpacity="0.2" strokeWidth="2.5" strokeLinecap="round" fill="none" />
            <circle cx="70" cy="7" r="2" fill="var(--primary)" />
          </svg>

          <h2 className="spark-reveal font-normal text-[var(--primary)] leading-[1.08] tracking-tight !font-sans" style={{
          opacity: 0,
          fontSize: "clamp(2rem, 4.2vw, 3.75rem)"
        }}><span className="!text-6xl"><span className="!text-7xl"><span className="!text-5xl">No panic.<br />No judgment.<br />Just useful information.</span></span></span></h2>

          <p className="spark-reveal mt-6 text-[var(--muted-foreground)] leading-relaxed" style={{
          opacity: 0,
          fontSize: "clamp(1rem, 1.1vw, 1.0625rem)"
        }}>Three pillars. One purpose — helping families make smarter, balanced screen choices.</p>
        </div>

        

        <div className="spark-cards mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-8">
          {cards.map((card, i) => {
          const Icon = card.icon;
          return <div key={i} data-index={i} className={`spark-card ${card.bg} rounded-[32px] p-8 transition-all duration-400 ease-[cubic-bezier(.22,.61,.36,1)] hover:-translate-y-1 cursor-default`} style={{
            opacity: 0
          }}>
                <div className={`${card.iconBg} w-14 h-14 rounded-lg flex items-center justify-center mb-6 shadow-sm`} aria-hidden="true">
                  <Icon className="w-7 h-7 text-[var(--primary)]" strokeWidth={1.5} />
                </div>
                <h3 className="font-serif font-medium tracking-tight leading-tight !text-[rgba(249,253,255,1)]" style={{
              fontSize: "clamp(1.375rem, 2.1vw, 1.875rem)"
            }}>
                  {card.title}
                </h3>
                <p className="mt-4 opacity-85 leading-relaxed !text-[rgba(255,255,255,1)]" style={{
              fontSize: "1rem"
            }}>
                  {card.body}
                </p>
              </div>;
        })}
        </div>
      </div>
    </section>;
}